import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils.js';
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LAYERS } from 'emergency_data/src/components/Map/useDeckGL/Axon/useAxonGLNext.js';
import Portal from '@rsos/base-components/Portal';
import {
  COLLABORATOR_CAPABILITY,
  RTI_DOWNSTREAM_CAPABILITY,
} from '@rsos/capstone/src/constants/capabilities';
import { ANI_ALI } from '@rsos/components/capstone/Location/constants/dataSources';
import { getDataSource } from '@rsos/components/capstone/Location/helpers';
import { Dropdown } from '@rsos/components/capstone/base';
import { DropdownBody } from '@rsos/components/capstone/base/Dropdown/DropdownComponents';
import { EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import { ALI_UNDERSCORE } from '@rsos/constants/locationTypes';
import { GOOGLE, ESRI } from '@rsos/constants/mapProviders';
import useHasCapability from '@rsos/hooks/useHasCapability';
import useIsTF911Enabled from '@rsos/hooks/useIsTF911Enabled';
import useWhichReverseGeocodeService from '@rsos/hooks/useWhichReverseGeocodeService';
import {
  ESRI_GEOCODE_SERVICE_URL,
  reverseGeocodeLocationToAddress,
} from '@rsos/maps/interface/reverseGeocode/helpers';
import { RSOS_PLACE_SOURCE } from '@rsos/maps/interface/rsosGeocoder/rsosGeocoder';
import { selectGeocodeService } from '@rsos/reduxDataStore';
import { viewAllAlerts } from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import {
  getRTTCollaboratorData,
  viewAllCalls,
  setSelectedCallerID,
  resetNumberWithNewLocation,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  setSelectedLocation,
  clearSelectedLocation,
} from '@rsos/reduxDataStore/src/features/gisSearch/gisSearchSlice';
import getSuggestedAddresses, {
  getAddressFromMagicKey,
} from '@rsos/reduxDataStore/src/features/gisSearch/helpers';
import {
  AGENT_511_MAP_CARD,
  AVIVE_APP_LAUNCHER_MAP_CARD,
  RADE_MAP_CARD,
  setCardsToRender,
  setMapCardData,
  closeCard,
  AXON_MAP_CARD,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import type { TF911Conversation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911.typings';
import { useLazyGetConversationMetadataQuery } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import {
  getWhat3WordsLocation,
  resetWhat3WordsLocation,
  resetWhat3WordsLocationError,
} from '@rsos/reduxDataStore/src/features/what3words/what3wordsSlice';
import { setIRPVersion } from '@rsos/sinatra';
import { getUrlParameter } from '@rsos/utils';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import {
  formatInternationalPhoneNumberE164,
  formatInternationalPhoneNumberHuman,
  isValidInternationalPhoneNumber,
} from '@rsos/utils/internationalPhoneNumberUtils';
import { notifyError } from '@rsos/utils/toastUtils';
import useCenterPoint from '@rsos/utils/useCenterPoint';
import useCenterPointCurrentView from '@rsos/utils/useCenterPointCurrentView';
import useDebounce from '@rsos/utils/useDebounce';
import useIsIRP from '@rsos/utils/useIsIRP';
import { clearLocationError, fetchLocation } from '../../../actions';
import { FETCH_LOCATION_SUCCESS } from '../../../actions/actionTypes';
// types
import type { RootState } from '../../../app/rootReducer';
import { setSelectedInteragencyChat } from '../../../reduxDataStore/interagency/interagencySlice';
import { createDatasetsSelector, search } from '../../../utils/search';
import { CALLS, AXON } from '../../../utils/search/constants';
import {
  hidePinnedPopover,
  showPinnedPopover,
} from '../../Map/useDeckGL/MapPopover/mapPopoverSlice';
import { show as showRADE } from '../RADECard/radeSlice';
import { isExtraInfoOrMeta } from '../helpers';
// JS component — cast to accept arbitrary props
import _AdvancedSearchOptions from './AdvancedSearchOptions';
import {
  SearchBarWrapper,
  InputStyleWrapper,
} from './ConnectedUniversalSearch.styles';
import type {
  ConnectedUniversalSearchProps,
  HandleAxonSearchResult,
  LocationData,
  SearchOption,
  SearchResult,
  SearchResultItem,
  ResultsDropdownPosition,
} from './ConnectedUniversalSearch.types';
import { ResultsContainer } from './ResultsContainer';
import { SearchDropdownMenu } from './SearchDropdownMenu';
// JS components — cast to accept arbitrary props
import _UniversalSearch from './UniversalSearch';
import { SearchBarInnerWrapper as _SearchBarInnerWrapper } from './UniversalSearch.styles';
import { trackClickOnSuggestedLocation } from './gisSearchTracking';
import { hasErrorHelper } from './hasErrorHelper';
import {
  w3wInputHelper,
  isW3WSearchTerm,
  isValidADRSearchTerm,
  isADRSearchTerm,
  isLatLongSearchTerm,
  normalizeReverseGeocodeResponse,
} from './inputHelper';
import { reset } from './searchSlice';
import {
  trackQueryByW3W,
  trackQueryByMAFID,
  trackClickOnSearchResult,
  trackQueryFromSearchBar,
} from './universalSearchTrackings';

const AdvancedSearchOptions =
  _AdvancedSearchOptions as unknown as React.ComponentType<
    Record<string, unknown>
  >;

const UniversalSearch = _UniversalSearch as React.ComponentType<
  Record<string, unknown>
>;
const SearchBarInnerWrapper = _SearchBarInnerWrapper as React.ComponentType<
  Record<string, unknown> & { children: React.ReactNode }
>;

// Create the datasets selector outside the component to avoid recreating it
const datasetsSelector = createDatasetsSelector([CALLS, AXON]);

// characters to be removed from search term
export const IGNORED_SPECIAL_CHARACTERS = /[-+()\s]/g;

type W3WInputHelper = (
  searchType: string,
  searchTerm: string,
) => string | false;
type ReverseGeocodeLocationFn = (location: {
  latitude: number;
  longitude: number;
  service: unknown;
  serviceUrl?: string;
  token?: string | null;
}) => Promise<unknown>;
type NormalizeReverseGeocodeFn = (
  response: unknown,
  latitude: number,
  longitude: number,
) => Record<string, unknown>;

function ConnectedUniversalSearch({
  filteredCalls,
  hasManageAccess,
  productToken,
  setShowW3wCallPane,
  mafError,
  setMafError,
  adrError,
  setADRError,
  searchTerm,
  prevSearchTerm,
  searchType,
  setSearchTerm,
  setSearchType,
  userRoleName,
  isPremiumUser,
  hasMAaccess,
  what3words,
  numberWithNewLocation,
  selectedCallerID,
  locationError,
  hasGisSearchAccess,
  setActiveQueueTab,
}: ConnectedUniversalSearchProps) {
  const searchBarRef = useRef<HTMLInputElement>(null);
  const resultContainerRef = useRef<HTMLDivElement>(null);
  // Thunk-aware dispatch: many action creators in this file are JS thunks or RTK Query actions
  const dispatch = useDispatch() as unknown as (
    action: unknown,
  ) => Promise<Record<string, unknown>>;
  const { queryURI, irpVersion } = useIsIRP();

  const geocodeService = useSelector(selectGeocodeService);

  const queryCallerId = getUrlParameter('caller_id') || '';
  const isTF911Enabled = useIsTF911Enabled();
  const orgName = useSelector(
    (state: RootState) => state.sinatra.user.currentOrg?.name,
  );
  const [triggerGetConversationMetadata] =
    useLazyGetConversationMetadataQuery();

  const parseW3WInput = w3wInputHelper as unknown as W3WInputHelper;
  const getWhat3WordsLocationAction = getWhat3WordsLocation as unknown as (
    w3w: string,
  ) => unknown;
  const resetWhat3WordsLocationAction =
    resetWhat3WordsLocation as unknown as () => unknown;
  const reverseGeocodeAction =
    reverseGeocodeLocationToAddress as unknown as ReverseGeocodeLocationFn;
  const normalizeGeocodeResponse =
    normalizeReverseGeocodeResponse as unknown as NormalizeReverseGeocodeFn;

  useEffect(() => {
    setSearchTerm(queryCallerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isValidW3W, setIsValidW3W] = useState(true);
  const [isClickedFromFilter, setIsClickedFromFilter] = useState(false);
  const [isDropdownClicked, setIsDropdownClicked] = useState(false);
  const [isDropdownTriggeredByKey, setIsDropdownTriggeredByKey] =
    useState(false);

  const [mouseCursor, setMouseCursor] = useState(0);
  const [availableSearchOptions, setAvailableSearchOptions] = useState<
    SearchOption[]
  >([]); // this is for advanced search
  const what3wordsError = what3words?.error?.status;
  const mapCardData = useSelector(
    (state: RootState) => state.mapCard.mapCardData,
  );
  const mapCards = useSelector((state: RootState) => state.mapCard.cards);
  const minLength = 4; // start filtering when user type to 4 characters

  const [isLoading, setIsLoading] = useState(false);
  const [gisSearchResults, setGisSearchResults] = useState<SearchResultItem[]>(
    [],
  );
  const [filteredCallResults, setFilteredCallResults] = useState<
    SearchResultItem[]
  >([]);
  const [filteredOutboundSMSResults, setFilteredOutboundSMSResults] = useState<
    SearchResultItem[]
  >([]);
  const [filteredAxonResults, setFilteredAxonResults] = useState<
    SearchResultItem[]
  >([]);
  const conversations = useSelector(
    (state: RootState) => state.textFrom911.conversations,
  );
  const hasCollaborator = useHasCapability(
    COLLABORATOR_CAPABILITY,
  ).hasCapability;
  const hasRtiDownstream = useHasCapability(
    RTI_DOWNSTREAM_CAPABILITY,
  ).hasCapability;

  const service = useWhichReverseGeocodeService();

  /**
   * Use the stable datasets selector created outside the component
   */
  const searchDatasets = useSelector(datasetsSelector);
  const selectedLocation = useSelector(
    (state: RootState) => state.gisSearch.selectedLocation,
  ) as LocationData | null;
  const gisError = useMemo(() => {
    return (
      !isLoading &&
      !gisSearchResults.length &&
      !filteredCallResults.length &&
      !filteredAxonResults.length &&
      (adrError ||
        (mapCards[RADE_MAP_CARD] &&
          !!getMostRecentLoc(filteredCalls[selectedCallerID])))
    );
  }, [
    mapCards,
    adrError,
    filteredCalls,
    selectedCallerID,
    isLoading,
    gisSearchResults,
    filteredCallResults,
    filteredAxonResults,
  ]);

  const jurisdictionCenter = useCenterPoint();
  const mapViewCenter = useCenterPointCurrentView();

  // Use map view center, falling back to jurisdiction center when unavailable
  const { latitude, longitude } = {
    latitude: mapViewCenter.latitude ?? jurisdictionCenter.latitude,
    longitude: mapViewCenter.longitude ?? jurisdictionCenter.longitude,
  };

  const getAvailableSearchOptions = () => {
    const w3wOption: SearchOption = {
      name: 'Search W3W',
      prefix: '///',
    };
    const adrOption: SearchOption = {
      name: 'Search additional data by phone number',
      prefix: '/',
    };

    let searchOptions: SearchOption[] = [adrOption, w3wOption];

    if (hasMAaccess) {
      const option: SearchOption = {
        name: 'Search MedicAlert ID',
        prefix: '/maf',
      };
      searchOptions = [...searchOptions, option];
    }
    setAvailableSearchOptions(searchOptions);
  };

  // This block is for IRP v1 only
  useEffect(() => {
    getAvailableSearchOptions();

    // check if it is IRP
    if (isValidInternationalPhoneNumber(getUrlParameter('caller_id'))) {
      const callerId = getUrlParameter('caller_id');
      if (callerId) {
        dispatch(setIRPVersion('1'));
        if (irpVersion === '1') {
          searchLocation(callerId);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [irpVersion]);

  // IRP v2
  useEffect(() => {
    if (queryURI && !queryURI.includes('alert')) {
      const irpQuery = queryURI.split('://')[1];
      searchLocation(irpQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryURI]);

  useEffect(() => {
    if (searchType) {
      const updatedSearchterm = `${searchType} ${searchTerm}`;
      setSearchTerm(updatedSearchterm);
      searchBarRef.current?.focus();
    } else {
      if (prevSearchTerm !== searchTerm) {
        setSearchTerm(searchBarRef?.current?.value || '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchType]);

  const debouncedFilteredCalls = useDebounce(
    (term: string, datasets: unknown) => {
      const results = (search(
        term.replace(IGNORED_SPECIAL_CHARACTERS, ''),
        datasets,
      ) || []) as SearchResultItem[];
      const normalizedCallsResults = results.filter(
        (result: SearchResultItem) => {
          if (filteredCalls[result.data?._callId as string]) {
            return getMostRecentLoc(
              filteredCalls[result.data?._callId as string],
            );
          }
          return false;
        },
      );
      setFilteredCallResults(normalizedCallsResults);
      const normalizedAxonResults = results.filter(
        (result: SearchResultItem) =>
          !normalizedCallsResults.includes(result) &&
          result.data?.source === AXON,
      );
      setFilteredAxonResults(normalizedAxonResults);
      // If outbound SMS feature is enabled, filter the conversations to find any outbound SMS
      // messages that match the search term but are not already included in the call results.
      if (isTF911Enabled) {
        const normalizedOutboundSMSResults: SearchResultItem[] = (
          Object.entries(conversations) as [string, TF911Conversation][]
        )
          .filter(([callerID, conversation]) => {
            if (conversation.data) {
              return (
                !normalizedCallsResults.find(
                  (call: SearchResultItem) => call.data?._callId === callerID,
                ) && callerID.includes(term)
              );
            }
            return false;
          })
          .map(([callerID, conversation]): SearchResultItem => {
            return {
              loading: conversation.loading,
              error: conversation.error,
              ui: conversation.ui,
              data: {
                ...conversation.data,
                callerID,
              },
            };
          });
        setFilteredOutboundSMSResults(normalizedOutboundSMSResults);
      }
    },
    250,
    [filteredCalls, conversations],
  );

  const debouncedGetSuggestedAddress = useDebounce(
    async ({
      searchTerm: term,
      latitude: lat,
      longitude: lng,
      serviceUrl,
      token,
    }: {
      searchTerm: string;
      latitude: number;
      longitude: number;
      serviceUrl?: string;
      token?: string | null;
    }) => {
      if (!isValidInternationalPhoneNumber(term) && !term.startsWith('/')) {
        const result = await dispatch(
          getSuggestedAddresses?.initiate({
            searchTerm: term.toLowerCase(),
            latitude: lat,
            longitude: lng,
            serviceUrl,
            token,
          }),
        );
        setGisSearchResults((result?.data as SearchResultItem[]) || []);
        setIsLoading(false);
      } else {
        setGisSearchResults([]);
      }
    },
    500,
    [],
  );

  useEffect(() => {
    setIsLoading(true);
    setGisSearchResults([]);
    setFilteredCallResults([]);
    setFilteredOutboundSMSResults([]);
    if (
      searchTerm &&
      searchTerm.length >= minLength &&
      !searchTerm.includes('/maf') &&
      !isW3WSearchTerm(searchTerm) &&
      !isADRSearchTerm(searchTerm) &&
      !isLatLongSearchTerm(searchTerm)
    ) {
      setMouseCursor(0);
      // if selectedLocation.LongLabel is the searchTerm: break
      if (
        selectedLocation &&
        selectedLocation?.attributes?.LongLabel === searchTerm
      ) {
        setGisSearchResults([]);
        return;
      }
      // clear location and popover when user paste a number in
      if (selectedLocation) {
        dispatch(clearSelectedLocation());
        dispatch(hidePinnedPopover());
      }

      if (hasGisSearchAccess) {
        debouncedGetSuggestedAddress({
          searchTerm: searchTerm.trim(),
          latitude,
          longitude,
          serviceUrl: geocodeService?.gis_service_url,
          token: geocodeService?.token || null,
        });
      }
      debouncedFilteredCalls(searchTerm, searchDatasets);
    } else {
      dispatch(clearSelectedLocation());
      dispatch(hidePinnedPopover());

      if (!searchTerm) {
        setIsDropdownTriggeredByKey(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    // update result dropdown when new call comes in and matches searchTerm
    if (
      searchTerm &&
      searchTerm.length >= minLength &&
      !searchTerm.includes('/maf') &&
      !isW3WSearchTerm(searchTerm) &&
      !isADRSearchTerm(searchTerm) &&
      !isLatLongSearchTerm(searchTerm)
    ) {
      debouncedFilteredCalls(searchTerm, searchDatasets);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetsSelector]);

  const clearOutError = useCallback(() => {
    dispatch(clearLocationError());
    setIsValidW3W(true);
    dispatch(resetWhat3WordsLocationError());
    setMafError(null);
    setADRError(null);
    if (gisError) {
      dispatch(closeCard(RADE_MAP_CARD));
    }
  }, [dispatch, gisError, setADRError, setMafError]);

  /**
   * Handle address selection from dropdown.
   * The suggestion contains { text, magicKey, _serviceUrl, _token } from the /suggest endpoint.
   * We use the magicKey with the SAME service that generated it (_serviceUrl) to fetch
   * full address details with coordinates from /findAddressCandidates.
   */
  const handleSelectedAddress = async (suggestion: SearchResult) => {
    const { text, magicKey, _serviceUrl, _token } = suggestion;

    // Show the selected text immediately for better UX
    if (text) setSearchTerm(text);

    // Determine if this is a global (ESRI world) or local GIS search based on the service URL
    const isEsriWorld = _serviceUrl === ESRI_GEOCODE_SERVICE_URL;
    const isRsos = _serviceUrl === RSOS_PLACE_SOURCE;
    let gisSource = 'local';
    if (isEsriWorld) gisSource = 'esri_world';
    else if (isRsos) gisSource = 'rsos';

    try {
      // Fetch full address details using magicKey with the same service that generated it
      const result = (await dispatch(
        getAddressFromMagicKey.initiate({
          magicKey,
          text,
          _serviceUrl,
          _token,
        }),
      )) as unknown as { data?: Record<string, unknown> };

      const loc = result?.data as Record<string, unknown> | undefined;

      if (loc) {
        // Local GIS coordinates may need web mercator conversion. Esri world
        // and RapidSOS results are already longitude/latitude.
        if (!isEsriWorld && !isRsos) {
          const location = loc.location as { x: number; y: number };
          const [lngVal, latVal] = webMercatorUtils.xyToLngLat(
            location.x,
            location.y,
          );
          const normalizedLoc: Record<string, unknown> = {
            ...loc,
            location: { x: lngVal, y: latVal },
          };
          dispatch(setSelectedLocation(normalizedLoc));
          setSearchTerm(
            ((normalizedLoc.attributes as Record<string, unknown>)
              ?.LongLabel as string) ||
              text ||
              '',
          );
          dispatch(showPinnedPopover({ loc: normalizedLoc }));
        } else {
          dispatch(setSelectedLocation(loc));
          setSearchTerm(
            ((loc.attributes as Record<string, unknown>)
              ?.LongLabel as string) ||
              text ||
              '',
          );
          dispatch(showPinnedPopover({ loc }));
        }

        const gisType = (loc.attributes as Record<string, unknown>)
          ?.Addr_type as string;
        trackClickOnSuggestedLocation(irpVersion, gisSource, gisType);
      }
    } catch (error) {
      // If fetching fails, keep the suggestion text displayed
      // eslint-disable-next-line no-console -- error visibility and Sentry breadcrumbs
      console.error('Failed to fetch address details:', error);
    }

    clearOutError();
  };

  const previousSelectedCallerIDRef = useRef(selectedCallerID);
  useEffect(() => {
    const prev = previousSelectedCallerIDRef.current;
    if (prev !== selectedCallerID) {
      clearOutError();
    }
    previousSelectedCallerIDRef.current = selectedCallerID;
  }, [selectedCallerID, clearOutError]);

  useEffect(() => {
    if (numberWithNewLocation !== null) {
      const irpQuery = queryURI ? queryURI.split('://')[1] : '';
      const isManualSearch = filteredCalls[numberWithNewLocation]
        ? filteredCalls[numberWithNewLocation].isManualSearch
        : false;
      if (
        (isManualSearch || irpQuery === numberWithNewLocation) &&
        locationError === null
      ) {
        dispatch(setSelectedCallerID(numberWithNewLocation));
      }
      if (numberWithNewLocation === selectedCallerID) {
        clearOutError();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberWithNewLocation]);

  /**
   * Reset universal search box, clear all user input
   */
  const handleReset = useCallback(() => {
    if (searchBarRef.current) {
      searchBarRef.current.value = '';
    }
    setMouseCursor(0);
    clearOutError();
    setSearchTerm('');
  }, [clearOutError, setSearchTerm]);

  const handleSearchResult = (callerID: string) => {
    const locations = filteredCalls[callerID]?.core?.locations;
    const firstLocIdx = locations?.length - 1 || 0;
    try {
      // when searching phone number without source, hitting 'enter' crashes.
      // this is a temporary fix to prevent the crash
      // TODO: JGAO to look into why this is happening
      const { payloadID, requestID } = isExtraInfoOrMeta(
        locations[firstLocIdx],
      );
      dispatch(setSelectedCallerID(callerID));
      trackClickOnSearchResult(irpVersion, payloadID, requestID);
      handleReset();
      setActiveQueueTab(EMERGENCY_CALLS);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error searching result', err);
    }
  };

  const handleSearchOutboundSMS = useCallback(
    (callerID: string) => {
      dispatch(setSelectedCallerID(callerID));
      handleReset();
      setActiveQueueTab(EMERGENCY_CALLS);
    },
    [dispatch, handleReset, setActiveQueueTab],
  );

  const handleResetToAllCalls = () => {
    dispatch(viewAllCalls());
  };

  const handleSearch = (term: string) => {
    if (selectedLocation && selectedLocation.attributes.LongLabel === term) {
      // prevent onSubmit from being called when selecting a suggestedAddress via key down
      return;
    }
    dispatch(resetNumberWithNewLocation());
    dispatch(setCardsToRender(RADE_MAP_CARD));
    dispatch(setMapCardData(null));
    if (term.includes('/maf')) {
      // The term comes with the searchType, so extract
      const mafSearchType = '/maf';
      let cleanTerm = '';
      const splitQuery = term.split(mafSearchType);
      if (splitQuery && splitQuery.length > 1) {
        cleanTerm = splitQuery[1].trim();
      }
      trackQueryByMAFID(irpVersion);
      handleResetToAllCalls();
      setSearchTerm(`${mafSearchType} ${cleanTerm}`);
      dispatch(showRADE());
    } else if (isW3WSearchTerm(term)) {
      const w3wSearchType = '///';
      const w3w = parseW3WInput(w3wSearchType, term.trim());
      if (w3w) {
        setSearchTerm(`${w3wSearchType} ${w3w}`);
        dispatch(getWhat3WordsLocationAction(w3w));
        dispatch(viewAllAlerts());
        dispatch(setSelectedInteragencyChat(null));
        const mostRecentLocation = getMostRecentLoc(
          filteredCalls[selectedCallerID],
        );
        if (
          (!selectedCallerID || (selectedCallerID && !mostRecentLocation)) &&
          !what3wordsError
        ) {
          setShowW3wCallPane(true);
        }
        trackQueryByW3W(irpVersion);
      } else {
        setIsValidW3W(false);
      }
    } else if (isADRSearchTerm(term)) {
      const ADR = isValidADRSearchTerm(term);
      if (ADR) {
        const adrSearchType = '/';
        handleResetToAllCalls();
        const formattedNumber = formatInternationalPhoneNumberHuman(ADR);
        setSearchTerm(`${adrSearchType} ${formattedNumber}`);
        dispatch(showRADE());
      } else {
        setADRError(true);
      }
    } else if (isLatLongSearchTerm(term)) {
      const latLong = isLatLongSearchTerm(term);
      if (latLong) {
        const location = {
          latitude: latLong[0],
          longitude: latLong[1],
          service,
          serviceUrl: geocodeService?.gis_service_url,
          token: geocodeService?.token || null,
        };

        reverseGeocodeAction(location)
          .then((response: unknown) => {
            const normalizedResponse = normalizeGeocodeResponse(
              response,
              location.latitude,
              location.longitude,
            );
            dispatch(setSelectedLocation(normalizedResponse));
            dispatch(
              showPinnedPopover({
                loc: normalizedResponse,
              }),
            );
          })
          .catch((err: unknown) => {
            // eslint-disable-next-line no-console
            console.log('error', err);
          });
      }
    } else if (term && searchType !== '/maf') {
      searchLocation(term.trim());
      dispatch(resetWhat3WordsLocationAction());
      dispatch(showRADE());
    }
  };

  /**
   * Handle Axon search result
   */
  const layerData = useSelector(
    (state: RootState) =>
      (state.schemas.byId as Record<string, unknown>)[LAYERS.AXON_BODY_3],
  );

  const handleAxonSearchResult: HandleAxonSearchResult = ({
    coordinates: rawCoordinates,
    axon,
  }) => {
    const coordinates = rawCoordinates as number[] | undefined;
    try {
      const mapProvider = window?.__RSOS?.map?.mapProvider;
      const map = window?.__RSOS?.map?.mapInstance as
        | Record<string, unknown>
        | undefined;
      if (mapProvider === GOOGLE) {
        (
          map as { panTo?: (pos: { lat: number; lng: number }) => void }
        )?.panTo?.({ lat: coordinates?.[1] ?? 0, lng: coordinates?.[0] ?? 0 });
        (map as { setZoom?: (level: number) => void })?.setZoom?.(17);
      } else if (mapProvider === ESRI) {
        const esriMap = map as
          | { view?: { zoom: number; center: unknown } }
          | undefined;
        if (esriMap?.view) {
          esriMap.view.zoom = 16;
          esriMap.view.center = coordinates;
        }
      }

      dispatch(
        setMapCardData({
          layerData,
          spatialObject: axon,
        }),
      );
      dispatch(setCardsToRender(AXON_MAP_CARD));

      handleReset();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error panning to Axon coordinates', err);
    }
  };

  /**
   * Legacy query view manual search location
   */
  function searchLocation(callerID: string) {
    if (!isClickedFromFilter) {
      // limit to only for QV, not from clicking from the filter list
      const token = productToken || localStorage.getItem('productToken');
      const normalizedCallerID =
        formatInternationalPhoneNumberE164(callerID)?.slice(1) || callerID;

      if (normalizedCallerID) {
        if (token) {
          setSearchTerm(callerID);
          dispatch(fetchLocation(normalizedCallerID, token))
            .then((res: Record<string, unknown>) => {
              if (
                res?.action &&
                (res?.action as Record<string, unknown>)?.type ===
                  FETCH_LOCATION_SUCCESS
              ) {
                const { requestID, payloadID } = res;
                const mostRecentLocation = getMostRecentLoc(
                  filteredCalls[normalizedCallerID],
                );
                const isAniAli = mostRecentLocation
                  ? mostRecentLocation.application?.startsWith(ALI_UNDERSCORE)
                  : false;
                const serviceType = isAniAli
                  ? ANI_ALI
                  : mostRecentLocation
                    ? getDataSource(mostRecentLocation)
                    : null;
                const classOfServices =
                  mostRecentLocation && 'class_of_service' in mostRecentLocation
                    ? mostRecentLocation.class_of_service
                    : null;

                trackQueryFromSearchBar(
                  userRoleName,
                  irpVersion,
                  payloadID,
                  requestID,
                  serviceType,
                  classOfServices,
                );
              }
              // Check for existing text from 911 conversation metadata if TF911 is enabled
              if (isTF911Enabled && orgName) {
                const digitCount = normalizedCallerID.replace(/\D/g, '').length;
                if (
                  /[^0-9+-]/.test(normalizedCallerID) ||
                  digitCount < 10 ||
                  digitCount > 15
                ) {
                  setADRError(true);
                  if (searchBarRef?.current?.focus) {
                    searchBarRef.current.focus();
                  }
                  return;
                }

                triggerGetConversationMetadata({
                  callerID: normalizedCallerID,
                  orgName,
                  custom: { isOutboundSMS: true },
                });
              }
            })
            .then(() => {
              if (hasCollaborator || hasRtiDownstream) {
                dispatch(
                  getRTTCollaboratorData({
                    callerID: normalizedCallerID,
                    orgName,
                  }),
                ); // check for collaborator or rti_downstream perms and capability
              }
            })
            .finally(() => {
              dispatch(setSelectedCallerID(normalizedCallerID));
            });
          if (searchBarRef.current) {
            searchBarRef.current.value = callerID;
          }
        } else if (!token && hasManageAccess) {
          notifyError(
            "Your agency's jurisdiction has not been approved yet. Approval can take up to 15 days.",
          );
        } else if (!token && !hasManageAccess) {
          const errMsg = () => {
            return (
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    'An error occurred, please try again.' +
                    ' If this error persists, submit a ticket via the "Support" tab.',
                }}
              ></div>
            );
          };
          notifyError({ message: errMsg() });
        }
      } else if (!callerID && searchTerm) {
        dispatch(reset());
      }
    }
  }

  const closeDropdown = (onClick: () => void) => {
    setIsDropdownClicked(false);
    onClick();
  };

  //TODO: Back trace these errors and remove unused ones from the app
  const { hasError, displayResultContainer } = hasErrorHelper({
    hasGisSearchAccess,
    searchTerm,
    minLength,
    searchType,

    gisError,
    adrError,
    mafError,
    what3wordsError,
    locationError, // not actually being used but kept for parity
    isValidW3W,

    selectedLocation,
  });

  useEffect(() => {
    // Reset the search if any map card has been opened
    // and selectedLocation is falsey
    if (
      !selectedLocation &&
      !gisSearchResults.length &&
      !filteredCallResults.length &&
      !filteredAxonResults.length &&
      ((mapCardData && Object.keys(mapCardData).length) ||
        mapCards[AVIVE_APP_LAUNCHER_MAP_CARD] ||
        mapCards[AGENT_511_MAP_CARD])
    ) {
      dispatch(reset());
      clearOutError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, mapCardData, mapCards]);

  const searchBarWrapperRef = useRef<HTMLDivElement>(null);

  const [resultsDropdownPosition, setResultsDropdownPosition] =
    useState<ResultsDropdownPosition>({
      top: 0,
      left: 0,
      width: 264,
    });

  useEffect(() => {
    if (displayResultContainer && searchBarWrapperRef.current) {
      const rect = searchBarWrapperRef.current.getBoundingClientRect();
      setResultsDropdownPosition({
        top: rect.bottom - 2,
        left: rect.left + 12,
        width: rect.width - 24,
      });
    }
  }, [displayResultContainer]);

  return (
    <>
      <SearchBarWrapper ref={searchBarWrapperRef}>
        <SearchBarInnerWrapper hasError={hasError}>
          <Dropdown
            zIndex={3}
            triggerElement={({
              onClick,
              selected,
            }: {
              onClick?: () => void;
              selected?: boolean;
            }) => (
              <SearchDropdownMenu
                onClick={onClick}
                selected={selected}
                searchTerm={searchTerm}
                isDropdownClicked={isDropdownClicked}
                isDropdownTriggeredByKey={isDropdownTriggeredByKey}
                onDropdownClicked={setIsDropdownClicked}
                onDropdownTriggeredByKey={setIsDropdownTriggeredByKey}
                setSearchTerm={setSearchTerm}
                clearOutError={clearOutError}
              />
            )}
            triggerOnClose
          >
            {(
              onDropdownClose: () => void,
              dropdownPosition?: { top: number; left: number; width: number },
            ) => (
              <DropdownBody>
                <AdvancedSearchOptions
                  availableSearchOptions={availableSearchOptions}
                  onSearchTypeChange={setSearchType}
                  closeDropdown={() => closeDropdown(onDropdownClose)}
                  top={(dropdownPosition?.top ?? 0) + 14}
                  left={(dropdownPosition?.left ?? 0) - 1}
                  width={dropdownPosition?.width}
                />
              </DropdownBody>
            )}
          </Dropdown>
          <InputStyleWrapper hasError={hasError}>
            <UniversalSearch
              clearOutError={clearOutError}
              handleSearchResult={handleSearchResult}
              handleSearchOutboundSMS={handleSearchOutboundSMS}
              hasError={hasError}
              mouseCursor={mouseCursor}
              searchBarRef={searchBarRef}
              searchLocation={searchLocation}
              searchTerm={searchTerm}
              searchType={searchType}
              setIsClickedFromFilter={setIsClickedFromFilter}
              setMouseCursor={setMouseCursor}
              setSearchTerm={setSearchTerm}
              onReset={handleReset}
              onSearch={handleSearch}
              handleSelectedAddress={handleSelectedAddress}
              resultContainerRef={resultContainerRef}
              gisSearchResults={gisSearchResults}
              filteredCallResults={filteredCallResults}
              filteredAxonResults={filteredAxonResults}
              filteredOutboundSMSResults={filteredOutboundSMSResults}
              handleAxonSearchResult={handleAxonSearchResult}
            />
          </InputStyleWrapper>
        </SearchBarInnerWrapper>
      </SearchBarWrapper>
      {displayResultContainer && (
        <Portal id="universal-search-results-portal" priority={1}>
          <ResultsContainer
            displayResultContainer={displayResultContainer}
            hasError={hasError}
            mafError={mafError}
            isValidW3W={isValidW3W}
            mouseCursor={mouseCursor}
            formatPhoneNumber={formatInternationalPhoneNumberHuman}
            handleSearchResult={handleSearchResult}
            handleSearchOutboundSMS={handleSearchOutboundSMS}
            isPremiumUser={isPremiumUser}
            hasGisSearchAccess={hasGisSearchAccess}
            handleSelectedAddress={handleSelectedAddress}
            resultContainerRef={resultContainerRef}
            gisSearchResults={gisSearchResults}
            filteredCallResults={filteredCallResults}
            filteredAxonResults={filteredAxonResults}
            filteredOutboundSMSResults={filteredOutboundSMSResults}
            top={resultsDropdownPosition.top}
            left={resultsDropdownPosition.left}
            width={resultsDropdownPosition.width}
            handleAxonSearchResult={handleAxonSearchResult}
          />
        </Portal>
      )}
    </>
  );
}

export default ConnectedUniversalSearch;
